import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Button } from './basics.js';
import { cx } from './cx.js';
import { Icon, type IconName } from './icon.js';

/**
 * Loading placeholder (ADR 126, D14): keeps the layout's shape while data loads,
 * so nothing jumps. Decorative; the surrounding region carries `aria-busy`.
 */
export function Skeleton({
  kind = 'text',
  lines = 3,
  className,
}: {
  kind?: 'text' | 'line' | 'block' | 'card' | 'table-row';
  lines?: number;
  className?: string;
}) {
  if (kind === 'text')
    return (
      <div className={cx('skeleton-text', className)} aria-hidden="true">
        {Array.from({ length: lines }, (_, i) => (
          <span className="skeleton line" key={i} />
        ))}
      </div>
    );
  if (kind === 'table-row')
    return (
      <div className={cx('skeleton-row', className)} aria-hidden="true">
        <span className="skeleton line" />
        <span className="skeleton line" />
        <span className="skeleton line" />
      </div>
    );
  return <span className={cx('skeleton', kind, className)} aria-hidden="true" />;
}

/**
 * Skeleton set (plan 45 §2, D28): one placeholder per layout, each the size of
 * what replaces it, so the page does not shift (CLS ≤ 0.05). All are
 * decorative; the region that loads carries `aria-busy` and a status line.
 */
export function SkeletonKpis({ count = 4 }: { count?: number }) {
  return (
    <div className="grid g4" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div className="card kpi skeleton-kpi" key={i}>
          <span className="skeleton line w-40" />
          <span className="skeleton figure" />
          <span className="skeleton line w-60" />
        </div>
      ))}
    </div>
  );
}

/** Rows of a list: avatar, two lines, a meta on the right. */
export function SkeletonList({ rows = 5 }: { rows?: number }) {
  return (
    <div className="skeleton-list" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div className="skeleton-item" key={i}>
          <span className="skeleton avatar-shape" />
          <span className="skeleton-text">
            <span className="skeleton line w-60" />
            <span className="skeleton line w-80" />
          </span>
          <span className="skeleton line w-meta" />
        </div>
      ))}
    </div>
  );
}

/** A table: header band and rows at the table's row height. */
export function SkeletonTable({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="skeleton-table" aria-hidden="true">
      {Array.from({ length: rows + 1 }, (_, r) => (
        <div className={cx('skeleton-trow', r === 0 && 'head')} key={r}>
          {Array.from({ length: columns }, (_, c) => (
            <span className="skeleton line" key={c} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** A detail panel: title, a few fields and the action row. */
export function SkeletonDetail() {
  return (
    <div className="skeleton-detail" aria-hidden="true">
      <span className="skeleton line w-60 tall" />
      <span className="skeleton line w-40" />
      <span className="skeleton block" />
      <span className="skeleton line w-80" />
      <span className="skeleton line w-80" />
      <span className="skeleton control" />
    </div>
  );
}

export type RouteSkeletonLayout =
  'dashboard' | 'list' | 'table' | 'split' | 'board' | 'calendar' | 'detail';

function routeBody(layout: RouteSkeletonLayout) {
  switch (layout) {
    case 'dashboard':
      return (
        <div className="stack-16">
          <SkeletonKpis />
          <div className="card">
            <SkeletonList rows={4} />
          </div>
        </div>
      );
    case 'list':
      return (
        <div className="card">
          <SkeletonList rows={8} />
        </div>
      );
    case 'table':
      return <SkeletonTable rows={8} columns={4} />;
    case 'split':
      return (
        <div className="skeleton-split">
          <div className="card">
            <SkeletonList rows={8} />
          </div>
          <div className="card skeleton-split-detail">
            <SkeletonDetail />
          </div>
        </div>
      );
    case 'board':
      return (
        <div className="grid g4">
          {Array.from({ length: 4 }, (_, i) => (
            <div className="card" key={i}>
              <SkeletonList rows={3} />
            </div>
          ))}
        </div>
      );
    case 'calendar':
      return <span className="skeleton block skeleton-calendar" />;
    default:
      return (
        <div className="card">
          <SkeletonDetail />
        </div>
      );
  }
}

/**
 * What a route shows while its code loads (plan 45 phase 2): the page's real
 * heading, so focus and the document title land at once, and the layout's
 * skeleton at the size of what replaces it (CLS ≤ 0.05). Screen readers hear
 * "Loading <page>"; the shapes are decorative.
 */
export function RouteSkeleton({
  title,
  layout = 'detail',
}: {
  title: string;
  layout?: RouteSkeletonLayout;
}) {
  return (
    <div className="page" aria-busy="true" data-route-skeleton={layout}>
      <span className="sr-only" role="status">
        Loading {title}…
      </span>
      <div className="page-head">
        <div>
          <h1>{title}</h1>
          <p className="sub skeleton-sub" aria-hidden="true">
            <span className="skeleton line w-60" />
          </p>
        </div>
      </div>
      <div aria-hidden="true">{routeBody(layout)}</div>
    </div>
  );
}

/** For waits under a second inside a control (a button that is saving). */
export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span className="spinner" role="status">
      <span className="sr-only">{label}…</span>
    </span>
  );
}

/**
 * A load that failed (D14): the cause in plain words and a retry that repeats
 * the call. Never a dead end.
 */
export function ErrorState({
  title = 'This could not be loaded',
  children,
  onRetry,
  details,
}: {
  title?: string;
  children?: ReactNode;
  onRetry?: (() => void) | undefined;
  details?: string | undefined;
}) {
  return (
    <div className="errorstate" role="alert">
      <Icon name="warning" />
      <div>
        <h2>{title}</h2>
        {children && <p className="sub">{children}</p>}
        {details && <p className="help">{details}</p>}
        {onRetry && (
          <Button small icon="refresh" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * A short hint on hover and on keyboard focus; Escape hides it. The child is
 * the trigger and must be focusable (a button, a link).
 */
export function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  return (
    <span
      className="tip-wrap"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
      aria-describedby={id}
    >
      {children}
      <span role="tooltip" id={id} className={cx('tip', open && 'show')}>
        {text}
      </span>
    </span>
  );
}

export interface TextFlag {
  /** The words in the text that were flagged. */
  excerpt: string;
  /** Why, in words the reader uses. */
  reason: string;
}

/**
 * A text with its flagged words marked. Not colour only: each flagged word is
 * underlined, bold and followed by a number, the reason shows in a tooltip on
 * hover and keyboard focus, and the numbered list below repeats every reason in
 * plain text for screen readers and touch.
 */
export function FlaggedText({ text, flags }: { text: string; flags: TextFlag[] }) {
  const usable = flags.filter((f) => f.excerpt.trim() !== '');
  const found: { start: number; end: number; n: number }[] = [];
  const lower = text.toLowerCase();
  usable.forEach((f, i) => {
    const needle = f.excerpt.trim().toLowerCase();
    for (
      let at = lower.indexOf(needle);
      at !== -1;
      at = lower.indexOf(needle, at + needle.length)
    ) {
      if (!found.some((m) => at < m.end && at + needle.length > m.start))
        found.push({ start: at, end: at + needle.length, n: i + 1 });
    }
  });
  found.sort((a, b) => a.start - b.start);
  const parts: ReactNode[] = [];
  let cursor = 0;
  found.forEach((m, i) => {
    if (m.start > cursor) parts.push(text.slice(cursor, m.start));
    parts.push(
      <Tooltip key={i} text={usable[m.n - 1]?.reason ?? ''}>
        <mark className="flag" tabIndex={0}>
          {text.slice(m.start, m.end)}
          <sup className="flag-n">
            <span className="sr-only">Flag </span>
            {m.n}
          </sup>
        </mark>
      </Tooltip>,
    );
    cursor = m.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return (
    <div className="flagged">
      <p className="flagged-text">{parts}</p>
      {usable.length > 0 && (
        <ol className="flag-list">
          {usable.map((f, i) => (
            <li key={i}>
              <b>&ldquo;{f.excerpt}&rdquo;</b>: {f.reason}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export interface MenuEntry {
  id: string;
  label: string;
  icon?: IconName;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

/**
 * Overflow actions behind one button (WAI-ARIA menu button): arrows move,
 * Home/End jump, Escape closes and returns focus, letters jump to an item.
 */
export function Menu({
  label,
  items,
  icon = 'more',
}: {
  label: string;
  items: MenuEntry[];
  icon?: IconName;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    (list.current?.children[active] as HTMLElement | undefined)?.focus();
    const away = (e: MouseEvent) => {
      if (!list.current?.contains(e.target as Node) && e.target !== button.current) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open, active]);
  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  const keys = (e: KeyboardEvent) => {
    const n = items.length;
    if (e.key === 'ArrowDown') setActive((a) => (a + 1) % n);
    else if (e.key === 'ArrowUp') setActive((a) => (a - 1 + n) % n);
    else if (e.key === 'Home') setActive(0);
    else if (e.key === 'End') setActive(n - 1);
    else if (e.key === 'Escape' || e.key === 'Tab') return close();
    else if (/^[a-z0-9]$/i.test(e.key)) {
      const i = items.findIndex((it) => it.label.toLowerCase().startsWith(e.key.toLowerCase()));
      if (i >= 0) setActive(i);
      return;
    } else return;
    e.preventDefault();
  };
  return (
    <span className="menu-wrap">
      <button
        ref={button}
        type="button"
        className="iconbtn"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => {
          setActive(0);
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setActive(e.key === 'ArrowDown' ? 0 : items.length - 1);
            setOpen(true);
          }
        }}
      >
        <Icon name={icon} />
      </button>
      {open && (
        <div ref={list} id={id} role="menu" aria-label={label} className="menu" onKeyDown={keys}>
          {items.map((it, i) => (
            <button
              key={it.id}
              type="button"
              role="menuitem"
              className={cx(it.danger && 'danger')}
              tabIndex={i === active ? 0 : -1}
              disabled={it.disabled}
              onClick={() => {
                close();
                it.onSelect();
              }}
            >
              {it.icon && <Icon name={it.icon} size={16} />}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

export interface Step {
  id: string;
  label: string;
}

/**
 * Where the person is in a flow of 3 to 7 steps (D14). Done, current and next
 * read as words too. `vertical` is the sign-up column of the canvas; `inverse`
 * sets it on the navy panel (D29). `complete` marks every step done once the
 * flow has finished.
 */
export function Stepper({
  steps,
  current,
  label,
  orientation = 'horizontal',
  inverse = false,
  complete = false,
}: {
  steps: Step[];
  current: string;
  label: string;
  orientation?: 'horizontal' | 'vertical';
  inverse?: boolean;
  complete?: boolean;
}) {
  const index = complete ? steps.length : steps.findIndex((s) => s.id === current);
  return (
    <ol
      className={cx('stepper', orientation === 'vertical' && 'vertical', inverse && 'inverse')}
      aria-label={label}
    >
      {steps.map((s, i) => {
        const state = i < index ? 'done' : i === index ? 'current' : 'next';
        return (
          <li key={s.id} className={state} aria-current={state === 'current' ? 'step' : undefined}>
            <span className="num" aria-hidden="true">
              {state === 'done' ? <Icon name="check" size={16} /> : i + 1}
            </span>
            <span>
              {s.label}
              <span className="sr-only">
                {state === 'done' ? ', done' : state === 'current' ? ', current step' : ''}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** The canvas name for the sign-up steps; same component as `Stepper`. */
export const StepIndicator = Stepper;

export interface Crumb {
  label: string;
  href?: string;
}

/** Path for pages three or more levels deep; the last crumb is the page. */
export function Breadcrumbs({
  items,
  renderLink,
}: {
  items: Crumb[];
  renderLink?: (href: string, children: ReactNode) => ReactNode;
}) {
  return (
    <nav aria-label="Breadcrumb" className="crumbs">
      <ol>
        {items.map((c, i) => {
          const last = i === items.length - 1;
          return (
            <li key={`${c.label}-${i}`} aria-current={last ? 'page' : undefined}>
              {c.href && !last
                ? (renderLink?.(c.href, c.label) ?? <a href={c.href}>{c.label}</a>)
                : c.label}
              {!last && <Icon name="chevron-right" size={16} />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** "Load more" for lists the API pages by cursor. Says how many are shown. */
export function LoadMore({
  shown,
  hasMore,
  loading,
  onMore,
  noun = 'items',
}: {
  shown: number;
  hasMore: boolean;
  loading?: boolean;
  onMore: () => void;
  noun?: string;
}) {
  return (
    <div className="loadmore">
      <span className="help" role="status">
        {shown} {noun} shown{hasMore ? '' : ', that is all'}
      </span>
      {hasMore && (
        <Button small onClick={onMore} disabled={loading} aria-busy={loading}>
          {loading ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </div>
  );
}

/**
 * Contextual save bar (Polaris): appears only while a form is dirty, sticks to
 * the bottom, and names the two outcomes.
 */
export function SaveBar({
  dirty,
  saving,
  onSave,
  onDiscard,
  message = 'Unsaved changes',
}: {
  dirty: boolean;
  saving?: boolean;
  onSave: () => void;
  onDiscard: () => void;
  message?: string;
}) {
  if (!dirty) return null;
  return (
    <div className="savebar" role="region" aria-label="Unsaved changes">
      <span>{message}</span>
      <div className="row">
        <Button onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
        <Button variant="primary" onClick={onSave} disabled={saving} aria-busy={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export interface FormProblem {
  /** The id of the field the message belongs to. */
  field: string;
  message: string;
}

/**
 * GOV.UK error summary: after a failed submit, every problem in one list at the
 * top, focused, each linking to its field.
 */
export function ErrorSummary({
  problems,
  title = 'There is a problem',
}: {
  problems: FormProblem[];
  title?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const shown = useRef<FormProblem[]>([]);
  useEffect(() => {
    const before = shown.current;
    shown.current = problems;
    // A check (submit) moves focus to the summary. A problem fixed while the person
    // types only shrinks the list, and focus stays in the field (WCAG 3.2.2).
    const fewer =
      before.length > problems.length &&
      problems.every((p) => before.some((b) => b.field === p.field));
    if (problems.length && !fewer) ref.current?.focus();
  }, [problems]);
  if (problems.length === 0) return null;
  return (
    <div ref={ref} className="errsum" role="alert" tabIndex={-1}>
      <h2>{title}</h2>
      <ul>
        {problems.map((p) => (
          <li key={p.field}>
            <a
              href={`#${p.field}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(p.field)?.focus();
              }}
            >
              {p.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A section wrapper that announces loading to assistive technology (D14). */
export function Busy({
  busy,
  children,
  ...rest
}: { busy: boolean; children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div aria-busy={busy || undefined} {...rest}>
      {children}
    </div>
  );
}

export interface ProgressStep {
  id: string;
  label: string;
}

/**
 * D60 (ADR 248, finding 003): a job the person waits for, shown as it really goes: a bar,
 * the steps ticked as they finish, the one under way with a spinner and the rest
 * still to come. The current step is announced politely, so nobody has to watch.
 */
export function ProgressSteps({
  label,
  steps,
  current,
  done = false,
}: {
  label: string;
  steps: ProgressStep[];
  /** The step under way; unknown ids count as the first. */
  current: string;
  /** Every step finished. */
  done?: boolean;
}) {
  const at = done
    ? steps.length
    : Math.max(
        0,
        steps.findIndex((s) => s.id === current),
      );
  const percent = Math.round(((done ? steps.length : at + 0.5) / steps.length) * 100);
  const now = steps[at];
  return (
    <div className="progress-steps">
      <div
        className="progress-bar"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <span style={{ '--progress': `${percent}%` } as CSSProperties} />
      </div>
      <p className="progress-now" role="status" aria-live="polite">
        {!done && <span className="spinner" aria-hidden="true" />}
        {done ? 'All done.' : `${now?.label ?? steps[0]?.label ?? ''}…`}
      </p>
      <ol className="progress-list" aria-label={label}>
        {steps.map((s, i) => {
          const state = done || i < at ? 'done' : i === at ? 'doing' : 'todo';
          return (
            <li key={s.id} data-state={state}>
              <span className="progress-mark" aria-hidden="true">
                {state === 'done' ? (
                  <Icon name="check" size={16} />
                ) : state === 'doing' ? (
                  <span className="spinner" />
                ) : null}
              </span>
              <span>
                {s.label}
                <span className="sr-only">
                  {state === 'done' ? ', done' : state === 'doing' ? ', under way' : ', to come'}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
